import type { ReactNode } from "react";
import { cn } from "./cn";

export type Column<T> = {
  /** Stable key, also used as the label in the stacked mobile view. */
  key: string;
  header: string;
  align?: "left" | "right";
  /** Width/visibility classes applied to both the th and the td. */
  className?: string;
  cell: (row: T) => ReactNode;
  /** Omit this column from the stacked mobile view (usually a duplicate). */
  hideOnMobile?: boolean;
};

/**
 * One table definition renders two ways: a real <table> from `md` up, and a
 * stacked label/value card list below it. The exports had no mobile design at
 * all, so this is the component that closes that gap everywhere at once.
 */
export function DataTable<T extends { id: string }>({
  columns,
  rows,
  empty,
  mobileCard,
  caption,
  className,
}: {
  columns: Column<T>[];
  rows: T[];
  empty?: ReactNode;
  mobileCard?: (row: T) => ReactNode;
  caption?: string;
  className?: string;
}) {
  if (rows.length === 0) return <>{empty}</>;

  return (
    <div className={className}>
      {/* Table — md and up */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-full border-collapse text-left">
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "border-b border-line-soft px-6 pb-2.5 text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase",
                    column.align === "right" ? "text-right" : "text-left",
                    column.className,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="group border-b border-line-soft last:border-0 hover:bg-surface-sunken/50"
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      "px-6 py-3 align-middle text-xs text-ink-2",
                      column.align === "right" ? "text-right" : "text-left",
                      column.className,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stacked cards — below md */}
      <ul className="divide-y divide-line-soft md:hidden">
        {rows.map((row) => (
          <li key={row.id} className="px-5 py-4">
            {mobileCard ? (
              mobileCard(row)
            ) : (
              <dl className="grid gap-2">
                {columns
                  .filter((column) => !column.hideOnMobile)
                  .map((column) => (
                    <div
                      key={column.key}
                      className="flex items-start justify-between gap-4"
                    >
                      <dt className="text-2xs font-bold tracking-[0.06em] text-ink-4 uppercase">
                        {column.header}
                      </dt>
                      <dd className="min-w-0 text-right text-xs text-ink-2">
                        {column.cell(row)}
                      </dd>
                    </div>
                  ))}
              </dl>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
